import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { PointsStrategy } from '@ocx/graphql';
import { LoyaltyCurrencyIcon, useLoyaltyCurrencyFormat } from '@ocx/feat-loyalty-currency';
import { SizingMultiplier, Typography } from '@ocx/ui';
import { ModalFullScreenDrawer, ModalFullScreenHeader, ModalFullScreenContent } from '@ocx/ui-modals';

import { BackButtonComponent } from '../../components/Layout/NavigationBar/BackButton/BackButtonComponent';

type OfferDetailsModalBaseProps = {
  children: ReactNode;
  isOpen: boolean;
  onClose(): void;
  onExited(): void;
};

type OfferDetailsFullScreenLayoutProps = OfferDetailsModalBaseProps & {
  pointsStrategy: PointsStrategy;
  membershipPoints: number;
};

type OfferFullScreenContentWrapperProps = {
  children: ReactNode;
};

const OfferFullScreenContentWrapper = ({ children }: OfferFullScreenContentWrapperProps) => (
  <Box
    sx={(theme) => ({
      position: 'relative',
      width: '100%',
      height: '100vh',
      overflowY: 'auto',
      alignSelf: 'stretch',
      background: theme.palette.appBar.background,
    })}>
    {children}
  </Box>
);

export const SweepstakesOfferDetailsLayout = (props: OfferDetailsFullScreenLayoutProps) => {
  const { children, isOpen, onClose, onExited, pointsStrategy, membershipPoints } = props;
  const formatLoyaltyCurrency = useLoyaltyCurrencyFormat();

  const headerPoints = useMemo(() => {
    if (pointsStrategy === PointsStrategy.NotUsed) {
      return undefined;
    }

    // TODO: consider to create component for balance
    return (
      <Box display="flex" justifyContent="center" alignItems="center" px={SizingMultiplier.sm}>
        <Typography variant="subtitle2" component="span" mr={SizingMultiplier.xs}>
          {formatLoyaltyCurrency(membershipPoints)}
        </Typography>
        <LoyaltyCurrencyIcon size="small" />
      </Box>
    );
  }, [formatLoyaltyCurrency, membershipPoints, pointsStrategy]);

  return (
    <ModalFullScreenDrawer open={isOpen} onClose={onClose} onExited={onExited} bgcolor="appBar.background">
      <ModalFullScreenHeader onBackClick={onClose} rightSlot={headerPoints} isElevationDisabled />
      <ModalFullScreenContent safeAreaBottomDisabled>{children}</ModalFullScreenContent>
    </ModalFullScreenDrawer>
  );
};

export const OfferFullScreenLayout = (props: OfferDetailsModalBaseProps) => {
  const { children, isOpen, onClose, onExited } = props;

  return (
    <ModalFullScreenDrawer open={isOpen} onClose={onClose} onExited={onExited}>
      <OfferFullScreenContentWrapper>
        <ModalFullScreenHeader
          centerSlot={null}
          rightSlot={<BackButtonComponent onClick={onClose} type="close" />}
          position="absolute"
          transparent
          isElevationDisabled
        />
        {children}
      </OfferFullScreenContentWrapper>
    </ModalFullScreenDrawer>
  );
};

export const useOfferDetailsLayout = (params: { returnPath: string }) => {
  const { returnPath } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleExited = useCallback(() => {
    if (location.state?.shouldHistoryPopOnClose) {
      navigate(returnPath, { replace: true, state: { skipPageTracking: true } });
      navigate(-1);
      return;
    }
    navigate(returnPath, { replace: true });
  }, [location.state?.shouldHistoryPopOnClose, navigate, returnPath]);
  return { isOpen, handleClose, handleExited };
};
